// What the page is called, by the real name of what it shows (a workflow's, a
// run's, a subagent's): the browser tab's title on every signed-in page, with a
// run's state in front so a finished run shows from a background tab; and the
// heading of the pages whose visible title is a field or none (<PageName />).
// The reads are the ones the page itself makes, under the same keys.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useDeployments } from "@/api/deployments";
import { useProjects } from "@/api/projects";
import { useTask } from "@/api/tasks";
import { useWorkflowRun } from "@/api/workflowRuns";
import { useWorkflows } from "@/api/workflows";
import { runWord } from "@/features/workflows/list/runFilters";
import { runDetailWord } from "@/features/workflows/status";
import { useAgentDirectory } from "@/hooks/useAgentDirectory";

import { pageNameFor } from "./crumbs";
import { SETTINGS_SECTIONS } from "./navModel";

const RESERVED = new Set(["new", "runs", "jobs", "data", "saved-results", "published", "deployments"]);

const segment = (pathname: string, at: number) => {
  const raw = pathname.split("/")[at];
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/** The page's name and, on a run, the run's state in words ("Running"). */
export function usePageName(customerId: string): { name: string; state: string } {
  const location = useLocation();
  const { pathname, search } = location;
  const dir = useAgentDirectory(customerId || null);
  const second = pathname.startsWith("/workflows/") ? segment(pathname, 2) : null;
  const third = second ? segment(pathname, 3) : null;
  // A Team task is a run too; its own page shows the same name and state.
  const runId = second === "runs" || (second === "jobs" && third !== "new") ? third : null;
  const editing = second && !RESERVED.has(second) ? second : null;
  const deployment = second === "deployments" ? third : null;
  const taskId = pathname.startsWith("/tasks/") ? segment(pathname, 2) : null;
  const project = pathname === "/projects" ? new URLSearchParams(search).get("project") : null;

  const workflows = useWorkflows(editing ? customerId : null).data?.workflows;
  const detail = useWorkflowRun(customerId, runId).data;
  const run = detail?.run;
  const task = useTask(customerId || null, taskId).data;
  const deployed = useDeployments(deployment && deployment !== "new" ? customerId : null).data;
  const projects = useProjects(project ? customerId : null).data?.projects;

  const nameOf = (template: string) => (dir.agents.length > 0 && !dir.byTemplate.has(template) && pathname.startsWith("/subagents/") ? "Not found" : dir.nameOf(template));
  const name = pageNameFor(location, nameOf, {
    // Known list, no such workflow: the tab says so, never a made-up title from the address.
    workflow: (slug) => (workflows ? (workflows.find((w) => w.slug === slug)?.name ?? "Not found") : undefined),
    // As the run's own header names it: a task's or a Team task's title, else its workflow.
    run: () => run?.task?.title?.trim() || run?.job?.brief.title?.trim() || run?.job?.brief.goal?.trim() || run?.workflow.name || undefined,
    task: () => task?.title,
    deployment: (n) => deployed?.find((d) => d.name === n)?.title,
    project: (id) => projects?.find((p) => p.id === id)?.name,
    settingsSection: (key) => SETTINGS_SECTIONS.find((s) => s.key === key)?.label,
  });
  // The run's own header's word: the job's mark, and the count of what failed.
  const state = detail ? (run?.status === "completed_with_failures" ? runWord({ status: run.status, job: detail.job, counters: detail.counters }) : runDetailWord(detail)).label : "";
  return { name, state };
}

/** "Alpha Researcher · Corvus", "Running: Alpha test of a dataset · Corvus". */
export function useDocumentTitle(customerId: string): void {
  const { name, state } = usePageName(customerId);
  useEffect(() => {
    document.title = name ? `${state ? `${state}: ` : ""}${name} · Corvus` : "Corvus";
  }, [name, state]);
}

/** The heading of a page whose visible title is a field (the editor) or absent (Projects). For screen readers and the page's outline. */
export function PageName({ customerId }: { customerId: string }) {
  const { name } = usePageName(customerId);
  return name ? <h1 className="sr-only">{name}</h1> : null;
}
